import { FC, useEffect, useState } from 'react';
import { RegistrationFormValues, StepProps } from './index';
import { Button, Col, Form, Input, Row, Collapse } from 'antd';
import { useTranslation } from 'react-i18next';
import { InfoCircleOutlined, LockOutlined } from '@ant-design/icons';
import { nameof } from 'src/shared/utils/objects';

import { Title, Text } from 'src/shared/components/typography';
import { J2LRole } from '../../shared/utils/user';

const Step3: FC<StepProps> = ({ onChange, onNext, loading, data }) => {
  const { password: dataPassword } = data;
  const { t } = useTranslation();

  const [meter, setMeter] = useState((dataPassword || '').length > 0);
  const [password, setPassword] = useState(dataPassword || '');

  const atLeastOneUppercase = /[A-Z]/g; // capital letters from A to Z
  const atLeastOneLowercase = /[a-z]/g; // small letters from a to z
  const atLeastOneNumeric = /[0-9]/g; // numbers from 0 to 9
  const atLeastOneSpecialChar = /[~@#_^*%/.+:;=,!?<>()[\]{}'"\\\-&|$]/g; // any of the special characters within the square brackets
  const eightCharsOrMore = /.{8,}/g; // eight characters or more

  const passwordTracker = {
    uppercase: password.match(atLeastOneUppercase),
    lowercase: password.match(atLeastOneLowercase),
    number: password.match(atLeastOneNumeric),
    specialChar: password.match(atLeastOneSpecialChar),
    eightCharsOrGreater: password.match(eightCharsOrMore)
  };

  const passwordStrength = Object.values(passwordTracker).filter((value) => value).length;

  const buildMessage = () => {
    if (passwordStrength >= 5) return '';
    const parts: string[] = [];

    // const x='Debe contener al menos   y un número. Puede incluir símbolos.'

    if (!passwordTracker.eightCharsOrGreater) parts.push(t('8 caracteres alfanuméricos'));
    if (!passwordTracker.uppercase) parts.push(t('una mayúscula'));
    if (!passwordTracker.lowercase) parts.push(t('una minúscula'));
    if (!passwordTracker.specialChar) parts.push(t('un caracter especial'));
    if (!passwordTracker.number) parts.push(t('un número'));

    const last = parts.pop();
    return t('Debe contener al menos ') + parts.join(', ') + (parts.length > 0 ? t(' y ') : '') + last;
  };

  useEffect(() => {
    onChange({ password });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  const onFinish = () => {
    onChange({ password });
    onNext({ password });
  };
  const role = data.role;
  const isPerson = role === J2LRole.Person;

  const backgroundColorDisabled = '#F1EDE9';
  const isDisabled = passwordStrength < 5 || loading;
  const buttonBackgroundColor = isDisabled ? backgroundColorDisabled : undefined;

  return (
    <div id="step3">
      <Title style={{ fontSize: 24 }} level={4}>
        {isPerson ? t('Únete al cambio. Date de alta.') : t('Únete y accede al mejor talento para tu proyecto.')}
      </Title>
      <Form id="registration" name="registration" initialValues={{ password }} onFinish={onFinish}>
        <Row gutter={[0, 15]} style={{ marginTop: isPerson ? 29 : 36 }}>
          <input hidden value={data.email} name="username" type="email" id="username" autoComplete="username" />

          <Col span={24}>
            <Form.Item
              style={{ marginBottom: !isPerson ? 8 : 0 }}
              extra={
                meter && (
                  <div>
                    <div className="password-strength-meter"></div>
                    <div style={{ fontSize: 13, color: '#7F88A1', lineHeight: 1.15 }}>{buildMessage()}</div>
                  </div>
                )
              }
              name={nameof<RegistrationFormValues>('password')}
              rules={[{ required: true, message: t('Por favor ingrese una contraseña') as string }]}
            >
              <Input.Password
                className="j2l-form-big-input"
                onFocus={() => setMeter(true)}
                onChange={(v) => setPassword(v.target.value)}
                disabled={loading}
                size="large"
                placeholder={t('Contraseña') as string}
                prefix={<LockOutlined />}
                type="password"
                name="password"
                id="password"
                autoComplete="new-password"
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Collapse
              className="password-suggestions-collapse"
              size="large"
              ghost
              expandIcon={() => <InfoCircleOutlined style={{ fontSize: 16 }} />}
              items={[
                {
                  key: '1',
                  label: (
                    <Text fontSize={16} underline>
                      {t('Crea una contraseña indescifrable')}
                    </Text>
                  ),
                  children: (
                    <ul>
                      <li>
                        <Text lh={1.15} fontSize={16}>
                          {t('Mejor única, sólo para jobs2live.')}
                        </Text>
                      </li>
                      <li>
                        <Text lh={1.15} fontSize={16}>
                          {t('Qué sea larga, cuanto más mejor. Y que combine letras, números y símbolos.')}
                        </Text>
                      </li>
                      <li>
                        <Text lh={1.15} fontSize={16}>
                          {t('No uses información personal como fechas o nombres.')}
                        </Text>
                      </li>
                      <li>
                        <Text lh={1.15} fontSize={16}>
                          {t('Una buena opción es usar una frase fácil de recordar. Por ejemplo "LaT0rtilla,SiempreConCebolla".')}
                        </Text>
                      </li>
                    </ul>
                  )
                }
              ]}
            />
          </Col>

          <Col span={24} style={{ marginTop: !isPerson ? 19 : 24 }}>
            <Button
              shape="round"
              style={{
                width: '100%',
                border: 'none',
                backgroundColor: buttonBackgroundColor,
                color: isDisabled ? '#CBCED4' : undefined
              }}
              size="large"
              type="primary"
              disabled={isDisabled}
              htmlType="submit"
            >
              {t('Dame de alta')}
            </Button>
          </Col>
        </Row>
      </Form>

      <style>
        {`
          .password-strength-meter::before {
            background-color: ${['red', 'orange', '#03a2cc', '#03a2cc', '#0ce052'][passwordStrength - 1] || ''};
            width: ${(passwordStrength / 5) * 100}%;
          }
        `}
      </style>
    </div>
  );
};

export default Step3;
